// ─── The customer booking journey ────────────────────────────────────────────
//
// "Choose service → choose available date/time → confirm client details →
// book/request appointment." (Original brief.)
//
// A state machine rather than a pile of component flags, because the rules
// worth getting right are about what may follow what — and those are much
// easier to see, and to test, written down in one place.
//
// ── The two rules that shape it ─────────────────────────────────────────────
//
// 1. ONLY GENUINELY AVAILABLE APPOINTMENTS ARE OFFERED. The slot list comes
//    from the availability engine, which subtracts everything that reserves
//    time. This machine never invents a slot and never re-derives one.
//
// 2. A SUBMITTED REQUEST MUST NOT DISAPPEAR — and must not double. The
//    `clientActionId` is generated ONCE, when the customer first taps the
//    button, and reused for every retry of that same tap. That is what makes
//    a lost connection safe: the module's replay protection returns the
//    original booking instead of creating a second.
//
//    Generating it per attempt would defeat the whole mechanism, which is why
//    it lives in the machine's state rather than being made at the call site.

/**
 * ── Why there is a "day" step ───────────────────────────────────────────────
 *
 * There was not one, and the slot step showed every free time for the whole
 * booking horizon in a single list — EIGHTEEN HUNDRED buttons at ninety days,
 * grouped by day and correct and unusable on a phone. The horizon was cut to
 * fourteen days as a stopgap, which only made it 290.
 *
 * Choosing a day first is how somebody actually books: they know roughly when
 * they want to come in, and then they want to see that day. It also means the
 * caller fetches one day's times instead of a quarter's.
 */
export const JOURNEY_STEPS = ["service", "day", "slot", "details", "review", "done"] as const;
export type JourneyStep = (typeof JOURNEY_STEPS)[number];

export type JourneyService = {
  id: string;
  name: string;
  durationMinutes: number;
  priceMinor?: number | null;
  currency?: string | null;
  description?: string | null;
};

export type JourneySlot = { resourceId: string; startsAt: Date; endsAt: Date };

/**
 * A day on the customer's month calendar.
 *
 * `openSlots` drives how free the day LOOKS — a coarse band of dots, never a
 * number on screen. `dayStateLabel` renders "Available" or "Fully booked" for
 * a customer and nothing numeric, because a slot count off a fifteen-minute
 * grid means nothing to the person reading it.
 */
export type JourneyDay = { date: Date; working: boolean; openSlots: number };

export type CustomerDetails = {
  /** Known for a returning customer, so they are not asked again. */
  personId?: string | null;
  fullName: string;
  phone: string;
  email?: string;
  notes?: string;
};

export type JourneyState = {
  step: JourneyStep;
  service: JourneyService | null;
  /** The day chosen on the month calendar. A wall clock in the business's
   *  zone, like every other Date that reaches these components. */
  day: Date | null;
  slot: JourneySlot | null;
  details: CustomerDetails | null;
  /** Stable across retries of the same tap. Created when the customer commits,
   *  never regenerated. */
  clientActionId: string | null;
  submitting: boolean;
  /** What went wrong, in words a customer can act on. */
  error: string | null;
  /** The booking, once it exists. */
  result: { bookingId: string; status: "requested" | "confirmed" } | null;
};

export function initialJourney(details?: CustomerDetails | null): JourneyState {
  return {
    step: "service",
    service: null,
    day: null,
    slot: null,
    // A returning customer's details are carried in, but they still confirm
    // them — reusing details is a convenience, not permission to book without
    // the person seeing what is about to happen.
    details: details ?? null,
    clientActionId: null,
    submitting: false,
    error: null,
    result: null,
  };
}

export type JourneyEvent =
  | { type: "pickService"; service: JourneyService }
  | { type: "pickDay"; day: Date }
  | { type: "pickSlot"; slot: JourneySlot }
  | { type: "setDetails"; details: CustomerDetails }
  | { type: "back" }
  | { type: "submit"; clientActionId: string }
  | { type: "submitted"; bookingId: string; status: "requested" | "confirmed" }
  | { type: "failed"; error: string }
  | { type: "restart" };

/** Whether the details given are enough to book with. */
export function detailsComplete(d: CustomerDetails | null): boolean {
  if (!d) return false;
  if (d.fullName.trim().length < 2) return false;
  // A phone number, because the fallback communication route is SMS and a
  // booking nobody can be told about is a booking that will be missed.
  return d.phone.replace(/[^0-9]/g, "").length >= 7;
}

const PREVIOUS: Partial<Record<JourneyStep, JourneyStep>> = {
  day: "service", slot: "day", details: "slot", review: "details",
};

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export function journeyReducer(state: JourneyState, event: JourneyEvent): JourneyState {
  switch (event.type) {
    case "pickService": {
      // Changing the service invalidates the slot: a two-hour colour cannot
      // keep the slot chosen for a one-hour cut. Silently keeping it would
      // offer a time the availability engine never said was free.
      //
      // The DAY survives, because "that day, but a colour instead" is an
      // ordinary thing to change your mind about and the day is still a
      // perfectly good question to have answered. The times for it get
      // re-asked either way.
      const same = state.service?.id === event.service.id;
      return {
        ...state, service: event.service,
        slot: same ? state.slot : null,
        step: "day", error: null,
      };
    }

    case "pickDay":
      return {
        ...state, day: event.day,
        // A different day cannot keep the time chosen on the old one.
        slot: state.day && sameDay(state.day, event.day) ? state.slot : null,
        step: "slot", error: null,
      };

    case "pickSlot":
      return { ...state, slot: event.slot, step: "details", error: null };

    case "setDetails":
      return {
        ...state, details: event.details,
        step: detailsComplete(event.details) ? "review" : "details",
        error: null,
      };

    case "back":
      if (state.submitting) return state; // never navigate out of an in-flight submit
      return { ...state, step: PREVIOUS[state.step] ?? state.step, error: null };

    case "submit":
      if (state.submitting || !state.service || !state.slot || !detailsComplete(state.details)) {
        return state;
      }
      return {
        ...state,
        submitting: true,
        error: null,
        // Kept if one already exists: this is a RETRY of the same tap, and
        // reusing the id is what stops it becoming a second booking.
        clientActionId: state.clientActionId ?? event.clientActionId,
      };

    case "submitted":
      return {
        ...state, submitting: false, step: "done", error: null,
        result: { bookingId: event.bookingId, status: event.status },
      };

    case "failed":
      // The action id survives a failure ON PURPOSE. If the request actually
      // reached the server before the connection dropped, retrying with the
      // same id returns that booking rather than making another.
      return { ...state, submitting: false, error: event.error };

    case "restart":
      return initialJourney(state.details);
  }
}

/** Whether the customer may move on from where they are. */
export function canAdvance(state: JourneyState): boolean {
  switch (state.step) {
    case "service": return !!state.service;
    case "day": return !!state.day;
    case "slot": return !!state.slot;
    case "details": return detailsComplete(state.details);
    case "review": return !state.submitting;
    case "done": return false;
  }
}

/** Human summary of what is about to be booked, for the review step. A person
 *  should see exactly what they are agreeing to before they agree to it. */
export function describeBooking(state: JourneyState, timeZone?: string): string | null {
  if (!state.service || !state.slot) return null;
  const when = state.slot.startsAt.toLocaleString("en-GB", {
    weekday: "long", day: "numeric", month: "long",
    hour: "2-digit", minute: "2-digit", hour12: false, timeZone,
  });
  return `${state.service.name}, ${state.service.durationMinutes} minutes, ${when}`;
}
